import React, { Context, createContext, useContext, useEffect } from 'react';
import { useCompanyContext } from './Company';
import { useLocation } from 'react-router';
import { getUserAttributes, isProd } from '../utils';
import { parse } from 'qs';
import { addNewEvent } from '../components/API/eventTracker';

export interface EventTrackerProps {
  trackEvent: (event: { event: string } & any) => void;
}

export const EventTracker: Context<EventTrackerProps> = createContext({
  trackEvent: (args) => {},
});

export function EventTrackerProvider({ children }) {
  const location = useLocation();
  const { business } = useCompanyContext();
  const urlParams = new URLSearchParams(window.location.search);

  async function trackEvent(event: object) {
    const prodEnv = isProd();
    let debugTracking = sessionStorage.getItem('debugTracking');
    if (!debugTracking && urlParams.get('debugTracking')) {
      sessionStorage.setItem('debugTracking', 'true');
    }

    if (prodEnv || debugTracking) {
      const leadId = window.localStorage.getItem('leadId');
      const user = await getUserAttributes();
      const dataLayer = window['dataLayer'];
      if (prodEnv) {
        dataLayer.push({
          user: user.email,
          email: user.email,
          company_id: business?.id,
          company_name: business?.name,
          page: location.pathname,
          url_params: parse(urlParams?.toString()),
          ...event,
        });
      }
      addNewEvent({
        leadId: leadId ?? undefined,
        companyId: business?.id,
        userId: user?.sub,
        userEmail: user.email,
        eventProps: event,
      });
    }
  }

  function trackPageChange() {
    trackEvent({
      event: 'page_change',
    });
  }

  async function trackRefresh() {
    // NEED TO GET USER HERE
    // ON REFRESH, WE DON'T HAVE ACCESS TO THE USER IN AUTH CONTEXT BEFORE THE EVENT IS FIRED
    const user = await getUserAttributes();
    if (user) {
      const userId = user['cognito:username'];
      const email = user.email;
      const firstName = user['custom:first_name'];
      const lastName = user['custom:last_name'];
      trackEvent({
        event: 'app_initialized',
        user_id: userId,
        user: email,
        user_name: `${firstName} ${lastName}`,
      });
    }
  }

  useEffect(() => {
    trackPageChange();
    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    trackRefresh();
    // eslint-disable-next-line
  }, []);

  return (
    <EventTracker.Provider
      value={{
        trackEvent,
      }}
    >
      {children}
    </EventTracker.Provider>
  );
}

export const useEventTrackerContext = () => useContext(EventTracker);
